import { Component, input, output, forwardRef, signal, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-rich-text-editor',
  imports: [CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextEditorComponent),
      multi: true
    }
  ],
  template: `
    <div class="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
      <div class="flex items-center gap-1 p-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <button type="button" (click)="format('bold')" class="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded" title="Bold">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 4h8a4 4 0 014 4v8a4 4 0 01-4 4H6z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 12h8" />
          </svg>
        </button>
        <button type="button" (click)="format('italic')" class="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded" title="Italic">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
          </svg>
        </button>
        <button type="button" (click)="format('underline')" class="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded" title="Underline">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
          </svg>
        </button>
      </div>
      <div
        #editor
        [class]="editorClasses"
        [contentEditable]="!disabled()"
        (input)="onInput()"
        [innerHTML]="value()"></div>
    </div>
  `,
  styles: []
})
export class RichTextEditorComponent implements ControlValueAccessor, AfterViewInit {
  placeholder = input<string>('Start typing...');
  disabled = input<boolean>(false);
  height = input<string>('200px');

  @ViewChild('editor', { static: false }) editor!: ElementRef<HTMLDivElement>;

  value = signal<string>('');
  touched = signal(false);

  private onChange = (value: string) => {};
  private onTouched = () => {};

  ngAfterViewInit(): void {
    if (this.editor) {
      this.editor.nativeElement.setAttribute('data-placeholder', this.placeholder());
    }
  }

  onInput(): void {
    if (this.editor) {
      const content = this.editor.nativeElement.innerHTML;
      this.value.set(content);
      this.onChange(content);
      this.markAsTouched();
    }
  }

  format(command: string): void {
    document.execCommand(command, false);
    this.onInput();
  }

  get editorClasses(): string {
    return `min-h-[200px] p-4 focus:outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100`;
  }

  private markAsTouched(): void {
    if (!this.touched()) {
      this.touched.set(true);
    }
  }

  writeValue(value: string): void {
    this.value.set(value || '');
    if (this.editor) {
      this.editor.nativeElement.innerHTML = value || '';
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Handled by input signal
  }
}

